import { ThemeProvider } from '@emotion/react';
import {
  StyledEngineProvider,
  GlobalStyles,
  Stack,
  Box,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { LoadingButton } from '@mui/lab';

// Local Imports
import { theme } from './common/theme';
import './logged-out.css';
import { images } from './assets/images';
import { useAppDispatch, useAppSelector } from './redux/store';
import { ContinueRef } from './onboarding/types';
import { OnboardingV2Step } from './redux/onboardingTypes';
import { onboardingActions } from './redux/onboarding';
import { useAppTransitions } from './ui';
import { useLoggedOutHeader } from './headerHooks';
import { appEnvironment } from './common/environment';

// Form Components
import SignUpForm, {
  TermsAndPrivacyNotice,
} from './components_v2/forms/SignUpForm';
import SelectCountryForm from './components_v2/forms/SelectCountryForm';
import CountryForm from './components_v2/forms/CountryForm';
import GenderForm from './components_v2/forms/GenderForm';
import SummaryV2Form from './components_v2/forms/SummaryV2Form';
import AddressV2 from './components_v2/forms/AddressvV2';
import StripeForm from './components_v2/forms/StripeForm';
import CheckoutForm from './components_v2/forms/CheckoutForm';
import SecondForm from './components_v2/forms/SecondForm';
import SYDLeftPanelContainer from './components_v2/SYDLeftPanelContainer';
import HeaderProgressBar from './components_v2/header/HeaderPogressBar';

// =============================================================================
// 1. Custom Hook: Logic Extraction
// =============================================================================

const useLoggedOutLogic = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  // --- Redux State ---
  const { buttonEnabled, onboardingStep, showingMessages } = useAppSelector(
    (state) => state.onboarding
  );
  const loading = useAppSelector((state) => state.checkout.loading);

  // --- Refs ---
  const leftPanelImageRef = useRef<HTMLImageElement>(null);
  const formRef = useRef<ContinueRef>(null);
  const hasRunAnimation = useRef(false);

  // --- Animation Hooks ---
  const { leftPanelTranslationX, animatedRightPanelInOnboarding } =
    useAppTransitions(leftPanelImageRef, true);

  // --- Header Logic ---
  const headerData = useLoggedOutHeader(); // { step, show, subtext }

  // --- Computed: Step Title ---
  const stepTitle = useMemo(() => {
    switch (onboardingStep) {
      case OnboardingV2Step.SIGN_UP:
        return t('completeTheFieldsBelow').replace(
          /syd/gi,
          appEnvironment.theme.appName
        );
      case OnboardingV2Step.SECOND_FORM:
        return t('yourDateOfBirth');
      case OnboardingV2Step.DELIVERY_ADDRESS:
        return t('whereWouldWeShipYourGeneticsTestingKit');
      default:
        return undefined;
    }
  }, [onboardingStep, t]);

  // --- Computed: Animation Phase Check ---
  const isAnimatingPhase = [
    OnboardingV2Step.ANIMATING,
    OnboardingV2Step.INTRO,
    OnboardingV2Step.WELCOME,
  ].includes(onboardingStep);

  // --- Effects: Animation State Management ---

  // Skip animation if disabled in environment
  useEffect(() => {
    if (
      !appEnvironment.theme.showOnboardingAnimation &&
      onboardingStep === OnboardingV2Step.ANIMATING
    ) {
      dispatch(onboardingActions.setButtonEnabled(true));
      dispatch(onboardingActions.setOnboardingStep(OnboardingV2Step.SIGN_UP));
    }
  }, [onboardingStep, dispatch]);

  // Trigger transition when reaching Welcome step
  useEffect(() => {
    if (
      !hasRunAnimation.current &&
      onboardingStep === OnboardingV2Step.WELCOME
    ) {
      hasRunAnimation.current = true;
      animatedRightPanelInOnboarding();
      dispatch(onboardingActions.setButtonEnabled(true));
      dispatch(onboardingActions.setOnboardingStep(OnboardingV2Step.INTRO));
    }
  }, [animatedRightPanelInOnboarding, dispatch, onboardingStep]);

  // --- Handlers ---
  const handleContinue = () => {
    // Disable button to prevent double clicks (except on payment step)
    if (onboardingStep !== OnboardingV2Step.PAYMENT_INFO) {
      dispatch(onboardingActions.setButtonEnabled(false));
    }
    if (formRef.current) {
      formRef.current.continuePressed();
    }
  };

  return {
    t,
    searchParams,
    // State
    onboardingStep,
    buttonEnabled,
    loading,
    showingMessages,
    isAnimatingPhase,
    // Refs
    leftPanelImageRef,
    formRef,
    // Animation
    leftPanelTranslationX,
    // Header Data
    headerData,
    stepTitle,
    // Actions
    handleContinue,
  };
};

// =============================================================================
// 2. Sub-Components: View Extraction
// =============================================================================

interface FormContentProps {
  step: OnboardingV2Step;
  formRef: React.RefObject<ContinueRef>;
}

const FormContent = ({ step, formRef }: FormContentProps) => {
  switch (step) {
    case OnboardingV2Step.SECOND_FORM:
      return <SecondForm ref={formRef} />;
    case OnboardingV2Step.COUNTRY:
      return <CountryForm ref={formRef} />;
    case OnboardingV2Step.COUNTRY_SELECT:
      return <SelectCountryForm />;
    case OnboardingV2Step.GENDER:
      return <GenderForm ref={formRef} />;
    case OnboardingV2Step.BILLING_ADDRESS:
      return <AddressV2 ref={formRef} type="billing" />;
    case OnboardingV2Step.DELIVERY_ADDRESS:
      return <AddressV2 ref={formRef} type="delivery" />;
    case OnboardingV2Step.PAYMENT_INFO:
      return <StripeForm ref={formRef} />;
    case OnboardingV2Step.SUMMARY:
      return <SummaryV2Form ref={formRef} />;
    case OnboardingV2Step.SIGN_UP:
    default:
      return <SignUpForm ref={formRef} />;
  }
};

interface ActionFooterProps {
  step: OnboardingV2Step;
  isAnimatingPhase: boolean;
  loading: boolean;
  showingMessages: boolean;
  buttonEnabled: boolean;
  onContinue: () => void;
  t: TFunction;
}

const ActionFooter = ({
  step,
  isAnimatingPhase,
  loading,
  showingMessages,
  buttonEnabled,
  onContinue,
  t,
}: ActionFooterProps) => {
  const isSyd = appEnvironment.theme.appName.toLowerCase() === 'syd';

  // Case 1: Entire Animation Phase (ANIMATING, WELCOME, INTRO) - Show Connection Text Only
  if (isAnimatingPhase) {
    return (
      <Stack className="w-full mb-[40px] z-20 relative" alignItems="center">
        <Typography className="font-medium text-[14px] text-[#26262680]">
          {t('establishingPrivateAndSecureConnection')}
        </Typography>
      </Stack>
    );
  }

  // Case 2: Standard Footer - Form active
  return (
    <Stack
      className="macbook13:my-0 macbook13:mb-[20px] my-[20px] z-20 relative"
      alignItems={'center'}
    >
      {/* Desktop Disclaimer: only for non-Syd brands (e.g. Altius) */}
      {!isSyd && (
        <span
          className="text-[12px] font-normal max-w-[700px] macbook13:hidden"
          style={{ color: appEnvironment.theme.colours.secondaryGrayText }}
        >
          {t('disclaimer').replace(/syd/gi, appEnvironment.theme.appName)}
        </span>
      )}

      {/* Mobile/Tablet Note */}
      <Typography component={'span'} className="signUp-note dark">
        <Typography component={'span'} className="signUp-note">
          98%
        </Typography>{' '}
        {t('percentOfMembers').replace(/syd/gi, appEnvironment.theme.appName)}
      </Typography>

      {/* Action Button */}
      <LoadingButton
        variant="contained"
        endIcon={
          loading || showingMessages ? undefined : (
            <Box component="img" src={images.v2.button.tick} />
          )
        }
        className={`button large bg-text ${
          step !== OnboardingV2Step.WELCOME ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onContinue}
        loading={loading || showingMessages}
        disabled={!buttonEnabled || showingMessages}
      >
        {step === OnboardingV2Step.SUMMARY
          ? t('completeSignUp')
          : isSyd && step === OnboardingV2Step.SIGN_UP
          ? t('createAccount', 'Create Account')
          : t('continue')}
      </LoadingButton>

      {/* Terms and Privacy notice below button for Syd on Sign Up step */}
      {isSyd && step === OnboardingV2Step.SIGN_UP && (
        <TermsAndPrivacyNotice className="mt-[14px] max-w-[500px]" />
      )}
    </Stack>
  );
};

// =============================================================================
// 3. Main Component
// =============================================================================

export default function LoggedOutV2() {
  const {
    t,
    searchParams,
    onboardingStep,
    buttonEnabled,
    loading,
    showingMessages,
    isAnimatingPhase,
    leftPanelImageRef,
    formRef,
    leftPanelTranslationX,
    headerData,
    stepTitle,
    handleContinue,
  } = useLoggedOutLogic();

  // 1. Left Panel (Visual Background)
  const leftPanel = (
    <Stack
      className={`fixed top-0 left-0 w-full h-full z-[-2] ${
        !isAnimatingPhase ? '' : 'transition-transform duration-1000'
      }`}
      justifyContent="center"
      alignItems="center"
      sx={{
        transform: `translateX(${leftPanelTranslationX}px)`,
      }}
    >
      <SYDLeftPanelContainer imageRef={leftPanelImageRef} />
    </Stack>
  );

  // 2. Right Panel (Forms)
  const rightPanel = (
    <Stack
      direction="row"
      justifyContent="center"
      alignItems="center"
      className="relative z-10 w-full mb-[10px] min-h-full flex-1"
    >
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        className="macbook13:mt-[0px] w-[700px]"
      >
        <FormContent step={onboardingStep} formRef={formRef} />
      </Stack>
    </Stack>
  );

  return (
    <StyledEngineProvider injectFirst={true}>
      <ThemeProvider theme={theme}>
        <GlobalStyles
          styles={{ body: { background: '#F5F6F6', padding: 0, margin: 0 } }}
        />

        <Stack className="root-container" sx={{ minHeight: '100vh' }}>
          {/* --- Header Section --- */}
          <Stack className="relative w-full justify-center my-[20px] z-20">
            {/* Logo: Absolute Top Left */}
            <Stack
              justifyContent={'center'}
              alignItems={'center'}
              className="absolute left-[40px] top-0 logo-container"
            >
              <Box
                component="img"
                src={appEnvironment.theme.images.logo}
                className="max-w-[50px] macbook13:max-w-[40px]"
              />
            </Stack>

            {/* Progress Bar (Centered) */}
            <Box
              className={
                headerData.show ? 'opacity-100' : 'opacity-0 invisible'
              }
            >
              <HeaderProgressBar
                label={t('signUp')}
                step={headerData.step}
                totalSteps={2}
                subtext={headerData.subtext}
                blue={onboardingStep === OnboardingV2Step.SUMMARY}
                stepTitle={stepTitle}
              />
            </Box>
          </Stack>

          {/* --- Main Content Area --- */}
          <Stack direction="row" className="w-full flex-1 relative">
            {/* Check for Stripe Payment Intent (Checkout Mode) */}
            {searchParams.get('payment_intent') !== null ? (
              <CheckoutForm />
            ) : (
              <>
                {/* Only render left panel if animation is enabled in theme */}
                {appEnvironment.theme.showOnboardingAnimation && leftPanel}

                {/* Hide right panel during animation phase */}
                {!isAnimatingPhase && rightPanel}
              </>
            )}
          </Stack>

          {/* --- Footer Section --- */}
          <ActionFooter
            step={onboardingStep}
            isAnimatingPhase={isAnimatingPhase}
            loading={loading}
            showingMessages={showingMessages}
            buttonEnabled={buttonEnabled}
            onContinue={handleContinue}
            t={t}
          />

          {/* --- Background Logos (Fixed Bottom) --- */}
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            className="fixed left-[20px] right-[40px] bottom-[20px] pointer-events-none z-[-1]"
          >
            <Box
              component={'img'}
              src={images.v2.onboarding.securityLogos}
              className="h-[40px]"
            />
            <Box
              component={'img'}
              className="h-[40px]"
              src={images.v2.onboarding.ratingLogos}
            />
          </Stack>
        </Stack>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
